/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {WithChildren} from '../../helpers'

export interface PageLink {
  title: string
  path: string
  isActive: boolean
  isSeparator?: boolean
}

export interface PageDataContextModel {
  pageTitle?: string
  setPageTitle: (_title: string) => void
  pageDescription?: string
  setPageDescription: (_description: string) => void
  pageBreadcrumbs?: Array<PageLink>
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
})

// Pages build their breadcrumb array on every render; two arrays with the
// same links are the same breadcrumbs (ADR-0022), so the provider is not
// updated for them.
const sameLink = (a: PageLink, b: PageLink) =>
  a === b ||
  (!!a && !!b &&
    a.title === b.title &&
    a.path === b.path &&
    !!a.isActive === !!b.isActive &&
    !!a.isSeparator === !!b.isSeparator)

const sameBreadcrumbs = (a?: Array<PageLink>, b?: Array<PageLink>) => {
  if (a === b) return true
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
  return a.every((link, i) => sameLink(link, b[i]))
}

const PageDataProvider: FC<WithChildren> = ({children}) => {
  const [pageTitle, setPageTitle] = useState<string>('')
  const [pageDescription, setPageDescription] = useState<string>('')
  const [pageBreadcrumbs, storeBreadcrumbs] = useState<Array<PageLink>>([])
  const setPageBreadcrumbs = useCallback(
    (next: Array<PageLink>) => storeBreadcrumbs((prev) => (sameBreadcrumbs(prev, next) ? prev : next)),
    []
  )
  const value: PageDataContextModel = useMemo(
    () => ({
      pageTitle,
      setPageTitle,
      pageDescription,
      setPageDescription,
      pageBreadcrumbs,
      setPageBreadcrumbs,
    }),
    [pageTitle, pageDescription, pageBreadcrumbs, setPageBreadcrumbs]
  )
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
}

function usePageData() {
  return useContext(PageDataContext)
}

type Props = {
  description?: string
  breadcrumbs?: Array<PageLink>
}

const PageTitle: FC<Props & WithChildren> = ({children, description, breadcrumbs}) => {
  const {setPageTitle, setPageDescription, setPageBreadcrumbs} = usePageData()
  useEffect(() => {
    if (children) {
      setPageTitle(children.toString())
    }
    return () => {
      setPageTitle('')
    }
  }, [children])

  useEffect(() => {
    if (description) {
      setPageDescription(description)
    }
    return () => {
      setPageDescription('')
    }
  }, [description])

  // Keyed on the links' value: a page re-render with an equal (but new)
  // array neither clears nor re-sets the breadcrumbs.
  const breadcrumbsKey = useMemo(
    () => (breadcrumbs ? JSON.stringify(breadcrumbs.map((b) => [b.title, b.path, !!b.isActive, !!b.isSeparator])) : ''),
    [breadcrumbs]
  )
  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs)
    }
    return () => {
      setPageBreadcrumbs([])
    }
  }, [breadcrumbsKey])

  return <></>
}

const PageDescription: FC<WithChildren> = ({children}) => {
  const {setPageDescription} = usePageData()
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString())
    }
    return () => {
      setPageDescription('')
    }
  }, [children])
  return <></>
}

export {PageDescription, PageTitle, PageDataProvider, usePageData, sameBreadcrumbs}
